// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import withBreadcrumb from 'react-router-breadcrumbs-hoc';
import { Breadcrumb } from 'antd';
import { useLocalMessage } from '@Helpers/';
import { RouteType } from '@Types';
import Routes from '@Pages/routes';
import AlertRoutes from '@Pages/Alerts/Details/routes';
import CaseRoutes from '@Pages/Cases/Details/routes';
import OrgRoutes from '@Pages/Admin/Organisations/Details/routes';
import TemplatesRoutes from '@Pages/Admin/Organisations/Details/Templates/routes';
import PlatformRoutes from '@Pages/Admin/PlatformManagement/Details/routes';
import EntitiesRoutes from '@Pages/Admin/Entities/Details/routes';

import './BreadcrumbManager.scss';

interface BreadcrumbManagerProps {
    breadcrumbs: any[];
    space?: string;
}

const BREADCRUMBS_PARAMS = Symbol('breadcrumbs');

const BreadcrumbManager = ({ breadcrumbs, space = '' }: BreadcrumbManagerProps): ReactElement => {
    const i = useLocalMessage();

    return (
        <div className="breadcrumb-manager">
            <Breadcrumb>
                {breadcrumbs.map((value) => {
                    const { label = '', isLink = true, forcedLink } = value[BREADCRUMBS_PARAMS] || {};
                    let url = value.key;

                    if (forcedLink) {
                        url = typeof forcedLink === 'string' ? forcedLink : forcedLink[space] || url;
                    }
                    const displayedLabel = typeof label === 'object' && label[space] ? label[space] || label : label;

                    return (
                        label && (
                            <Breadcrumb.Item key={displayedLabel}>
                                <span className="breadcrumb-manager__item">
                                    {isLink ? (
                                        <Link to={encodeURI(url)}>
                                            {typeof displayedLabel === 'string' ? i(displayedLabel) : displayedLabel}
                                        </Link>
                                    ) : typeof displayedLabel === 'string' ? (
                                        i(displayedLabel)
                                    ) : (
                                        displayedLabel
                                    )}
                                </span>
                            </Breadcrumb.Item>
                        )
                    );
                })}
            </Breadcrumb>
        </div>
    );
};

const rootRoutes = Object.values(Routes).reduce(
    (acc, currentRoute): RouteType[] => [...acc, ...currentRoute],
    [] as RouteType[],
);

const allRoutes = [
    ...rootRoutes,
    ...AlertRoutes,
    ...CaseRoutes,
    ...OrgRoutes,
    ...TemplatesRoutes,
    ...PlatformRoutes,
    ...EntitiesRoutes,
];

const extractAndConvertBreadcrumbData = (opt: RouteType): any => {
    const { breadcrumb, ...rest } = opt;
    return {
        [BREADCRUMBS_PARAMS]: breadcrumb,
        ...rest,
    };
};

export default withBreadcrumb(allRoutes.map(extractAndConvertBreadcrumbData))(BreadcrumbManager);
